import { useMemo } from "react";
import { useStoreSettings } from "@/hooks/useStoreSettings";

/** Convert a hex color (#RRGGBB) to HSL values string "H S% L%" */
function hexToHSL(hex: string): string | null {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return null;
  const r = parseInt(clean.substring(0, 2), 16) / 255;
  const g = parseInt(clean.substring(2, 4), 16) / 255;
  const b = parseInt(clean.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const fontFamilyMap: Record<string, string> = {
  nunito: "'Nunito', sans-serif",
  roboto: "'Roboto', sans-serif",
  "open-sans": "'Open Sans', sans-serif",
  lato: "'Lato', sans-serif",
  inter: "'Inter', sans-serif",
  "source-sans": "'Source Sans 3', sans-serif",
};

const fontImportMap: Record<string, string> = {
  nunito: "Nunito:wght@400;600;700",
  roboto: "Roboto:wght@400;500;700",
  "open-sans": "Open+Sans:wght@400;600;700",
  lato: "Lato:wght@400;700",
  inter: "Inter:wght@400;500;600;700",
  "source-sans": "Source+Sans+3:wght@400;600;700",
};

export default function DynamicTheme() {
  const { settings } = useStoreSettings();

  const css = useMemo(() => {
    const vars: string[] = [];

    if (settings.primary_color) {
      const hsl = hexToHSL(settings.primary_color);
      if (hsl) {
        vars.push(`--primary: ${hsl}`);
        vars.push(`--ring: ${hsl}`);
        vars.push(`--sidebar-primary: ${hsl}`);
      }
    }
    if (settings.secondary_color) {
      const hsl = hexToHSL(settings.secondary_color);
      if (hsl) vars.push(`--secondary: ${hsl}`);
    }
    if (settings.accent_color) {
      const hsl = hexToHSL(settings.accent_color);
      if (hsl) {
        vars.push(`--accent: ${hsl}`);
        vars.push(`--warning: ${hsl}`);
      }
    }

    // Fonts
    const primaryFamily = fontFamilyMap[settings.primary_font || "nunito"] || fontFamilyMap.nunito;
    const secondaryFamily = fontFamilyMap[settings.secondary_font || "inter"] || fontFamilyMap.inter;
    vars.push(`--font-primary: ${primaryFamily}`);
    vars.push(`--font-secondary: ${secondaryFamily}`);

    // Build import URLs
    const imports: string[] = [];
    const pKey = settings.primary_font || "nunito";
    const sKey = settings.secondary_font || "inter";
    if (fontImportMap[pKey]) imports.push(fontImportMap[pKey]);
    if (pKey !== sKey && fontImportMap[sKey]) imports.push(fontImportMap[sKey]);
    const importUrl = imports.length
      ? `@import url('https://fonts.googleapis.com/css2?${imports.map(f => `family=${f}`).join("&")}&display=swap');`
      : "";

    const rootBlock = vars.length ? `:root { ${vars.join("; ")} }` : "";
    const bodyFont = `body { font-family: ${primaryFamily}; } h1,h2,h3,h4,h5,h6 { font-family: ${primaryFamily}; }`;

    return [importUrl, rootBlock, bodyFont].filter(Boolean).join("\n");
  }, [settings.primary_color, settings.secondary_color, settings.accent_color, settings.primary_font, settings.secondary_font]);

  if (!css) return null;
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}
